import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import {subDays} from "date-fns";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import Filters from "./components/filters.jsx";
import Content from "./components/content.jsx";
import {useQuery} from "@tanstack/react-query";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import VehiclePassagesSearchService from "./components/services/vehicle-passages-search-service.js";
import {hasFilterValue} from "../../web/individual/list/resources/filter-utils.js";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subDays(new Date(), 7),
    dtEnd: new Date(),
    analysis: {label: "Resumida por usuário", value: 'user'},
    plate: '',
    groups: [],
    userLogin: [],
    userName: [],
    userCpf: []
});

const filterLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    analysis: "Tipo de análise",
    plate: "Placa",
    groups: "Grupo de operação",
    userLogin: "Login",
    userName: "Nome",
    userCpf: "CPF"
};

const initialTableColumns = {
    user_name: {label: "Agente", visible: true},
    date_search: {label: "D/H de pesquisa", visible: true},
    search: {label: "Placa pesquisada", visible: true},
    similar_plate: {label: "Caracteres similares", visible: true},
    correspondent_plate: {label: "Placas correspondentes", visible: true},
    date_range: {label: "Intervalo de tempo", visible: true},
    auto_search: {label: "Pesquisa automática", visible: true},
};

const VehiclePassagesSearch = () => {
    const [loading, setLoading] = useState(false);

    const [collapsed, setCollapsed] = useState(false);
    const buttonRef = useRef(null);
    const [fixedTop, setFixedTop] = useState(null);

    const [filters, setFilters] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);
    const [reportData, setReportData] = useState(null);
    const [tableColumns, setTableColumns] = useState(() => initialTableColumns);

    const [title] = useState("Auditoria de pesquisa de passagens");
    const [headerSelected, setHeaderSelected] = useState(-1);

    const {data: brandModels, isLoading: isLoading} = useQuery({
        queryKey: ['vehicle-passages-brand-models'],
        queryFn: () => VehiclePassagesSearchService.getBrandModelsTranslation(),
        enabled: appliedFilters.analysis.value === true
    });

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : ""
            }, filterLabels)
        );

    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const dateValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd);

            if (!dateValidation.ok) {
                ReportUtils.alertErrorMessage(dateValidation.message);
                setLoading(false);
                return;
            }

            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, hasFilterValue(filters.plate) ? 12 : 7, hasFilterValue(filters.plate) ? "months" : "days");

            if (!periodValidation.ok) {
                if (!hasFilterValue(filters.plate)) {
                    ReportUtils.alertErrorMessage("O período máximo de pesquisa é de 7 dias.<br>Informe uma placa para pesquisar até 12 meses.", "", true);
                    setLoading(false);
                    return;
                }
            }

            if (!ReportUtils.requiredFilterValidate(filters.analysis, "Tipo de análise", setLoading)) return;

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                analysis: filters.analysis.value ?? 'user',
                plate: filters.plate ?? '',
                groups: filters.groups?.map(i => i.value) ?? [],
                userLogin: filters.userLogin?.map(i => i.value) ?? [],
                userName: filters.userName?.map(i => i.value) ?? [],
                userCpf: filters.userCpf?.map(i => i.value) ?? []
            };

            const auditFilters = ReportUtils.filters({
                 ...filters,
                 dtStart: formattedFilters.dtStart,
                 dtEnd: formattedFilters.dtEnd},
             filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/vehicle_passages_search/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);
            setAppliedFilters(filters);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {

            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);

        } finally {
            setLoading(false);
        }
    };

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        setFilters(initialFilters);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);
    const analysisData = (reportData?.userAnalysis?.length ?? 0) + (reportData?.plateAnalysis?.length ?? 0) + (reportData?.completeAnalysis?.length ?? 0);
    const isUserAnalysis = appliedFilters.analysis?.value === 'user';

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className={"flex"}>

                <Template isLoading={loading ?? isLoading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                exportable={true}
                                exportInSheets={true}
                                printable={true}
                                headerChange={true}
                                visibilityColumns={isUserAnalysis}
                                columns={isUserAnalysis ? tableColumns : undefined}
                                setColumns={isUserAnalysis ? setTableColumns : undefined}
                                printComponentRef={printComponentRef}
                                reportData={reportData}
                                data={analysisData}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                        <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                              collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                            <Filters setFilters={setFilters} filters={filters}/>
                            <AutoDatetime setFilters={setFilters}/>

                        </Menu>
                    </Template.Body>
                </Template>

                <Card className={`w-full h-fit mr-2 ${!reportData || analysisData === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || analysisData === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || analysisData === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                            <NotAppliedContent reportData={reportData}/>
                            <NoDataContent reportData={reportData} data={analysisData}/>
                            <Content reportData={reportData} data={analysisData} brandModels={brandModels} analysis={appliedFilters.analysis.value} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} tableColumns={tableColumns}/>
                        </div>
                    </Card.Body>
                </Card>

            </div>
        </SubScroll>
    );
};

export default VehiclePassagesSearch;